import React, { useState, useRef, useEffect, useLayoutEffect } from 'react'
import { useDispatch } from 'react-redux'
import { messageBoxHideAction } from 'src/reducers/message-box/action'
import { Fade, withStyles } from '@material-ui/core'
import { MessageBoxHeader, MessageBoxButtons } from './components'
import styles from './styles'
import clsx from 'clsx'

const MessageBox = props => {
  const { classes, content, id } = props
  const dispatch = useDispatch()

  // Determine the variant and draggable state
  const variant = content.variant || 'normal'
  const isDraggable = variant === 'draggable' || content.draggable
  const isNotice = variant === 'notice'

  const [isDragging, setIsDragging] = useState(false)
  const [position, setPosition] = useState(null) // Start with null position
  const [initialPosition, setInitialPosition] = useState({ x: 0, y: 0 })
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const boxRef = useRef(null)
  const contentIdRef = useRef(null)

  // Reset position when content changes (new modal opened)
  useEffect(() => {
    if (content?.id !== contentIdRef.current) {
      contentIdRef.current = content?.id
      setPosition(null)
    }
  }, [content?.id])

  // Calculate initial position after first render using useLayoutEffect
  // This runs synchronously after DOM mutations but before browser paint
  useLayoutEffect(() => {
    if (boxRef.current && isDraggable && position === null) {
      const box = boxRef.current.getBoundingClientRect()
      const centerX = Math.max(0, (window.innerWidth - box.width) / 2)
      const centerY = Math.max(0, (window.innerHeight - box.height) / 2)
      setPosition({ x: centerX, y: centerY })
      setInitialPosition({ x: centerX, y: centerY })
    }
  }, [isDraggable, position]) // Re-run when position becomes null

  const onClose = () => {
    content.callback && content.callback()
    setPosition(null)
    dispatch(messageBoxHideAction(`message-box-${id}`))
  }

  const handleMouseDown = e => {
    if (!isDraggable) return
    if (e.target.closest('.message-box-header')) {
      setIsDragging(true)
      setDragStart({
        x: e.clientX - (position?.x || 0),
        y: e.clientY - (position?.y || 0),
      })
    }
  }

  const handleMouseMove = e => {
    if (isDragging && isDraggable && position) {
      const newX = e.clientX - dragStart.x
      const newY = e.clientY - dragStart.y

      const box = boxRef.current.getBoundingClientRect()
      const maxX = window.innerWidth - box.width
      const maxY = window.innerHeight - box.height

      setPosition({
        x: Math.min(Math.max(0, newX), maxX),
        y: Math.min(Math.max(0, newY), maxY),
      })
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  // Determine box styles based on variant
  const getBoxStyles = () => {
    if (isDraggable) {
      // If position is not set yet, use CSS to center (fallback)
      if (position === null) {
        return {
          position: 'fixed',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          userSelect: 'none',
          margin: 0,
          maxHeight: content.maxHeight ? content.maxHeight : 'unset',
          height: content.maxHeight ? '100%' : 'unset',
          maxWidth: content.maxWidth ? content.maxWidth : 'fit-content',
        }
      }

      // Once position is calculated, use absolute positioning
      return {
        position: 'fixed',
        left: 0,
        top: 0,
        transform: `translate(${position.x}px, ${position.y}px)`,
        userSelect: 'none',
        margin: 0,
        maxHeight: content.maxHeight ? content.maxHeight : 'unset',
        height: content.maxHeight ? '100%' : 'unset',
        maxWidth: content.maxWidth ? content.maxWidth : 'fit-content',
      }
    } else if (isNotice) {
      return {
        position: 'relative',
        maxWidth: content.maxWidth ? content.maxWidth : 'fit-content',
        width: '100%',
        minWidth: '320px',
        maxHeight: content.maxHeight ? content.maxHeight : 'unset',
        height: content.maxHeight ? '100%' : 'unset',
        overflow: content.maxHeight ? 'auto' : 'hidden',
      }
    } else {
      // Normal popup
      return {
        position: 'relative',
        maxWidth: content.maxWidth ? content.maxWidth : 'fit-content',
        width: '100%',
        minWidth: '400px',
        maxHeight: content.maxHeight ? content.maxHeight : 'unset',
        height: content.maxHeight ? '100%' : 'unset',
        overflow: content.maxHeight ? 'auto' : 'visible',
      }
    }
  }

  const boxStyles = getBoxStyles()

  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // onClose intentionally not included to avoid recreating listener

  const hideCloseButton = content.hideCloseButton

  return (
    <>
      {content && (
        <Fade in={true} mountOnEnter unmountOnExit>
          <div
            className={clsx({
              [classes.overlay]: true,
              [classes.draggable]: isDraggable,
              [classes.noticeOverlay]: isNotice,
            })}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            id={`message-box-${id}`}
          >
            <div
              ref={boxRef}
              className={clsx({
                [classes.boxDraggable]: isDraggable === true,
                [classes.box]: isDraggable !== true,
              })}
              onMouseDown={handleMouseDown}
              style={boxStyles}
            >
              <div
                className="message-box-header"
                style={{
                  cursor: isDraggable ? 'grab' : 'default',
                }}
              >
                {isDraggable === true && <div className={classes.dragLine} />}

                <MessageBoxHeader
                  title={content.title}
                  icon={content.type === 0 && '/assets/delete-icon.svg'}
                  onClose={onClose}
                  isDraggable={isDraggable}
                  content={content}
                  isDragging={isDragging}
                  hideCloseButton={hideCloseButton}
                />
              </div>

              {content.subTitle && (
                <div className={classes.subTitleStyle}>{content.subTitle}</div>
              )}
              {content.description && (
                <div className={classes.descriptionStyle}>
                  {content.description}
                </div>
              )}
              {content.note && (
                <div className={classes.noteStyle}>{content.note}</div>
              )}
              {content.component && <>{content.component}</>}
              {content.buttons?.length !== 0 && (
                <MessageBoxButtons buttons={content.buttons} />
              )}
            </div>
          </div>
        </Fade>
      )}
    </>
  )
}

export default withStyles(styles)(MessageBox)
